import { useEffect, useState, type ReactNode } from 'react';
import { RequestCheckout } from '../concierge/request_checkout.js';
import type { CheckoutIo } from '../model/checkout_env.js';
import type { CheckoutProductType, CheckoutTravelerRef } from '../model/checkout_source.js';
import { productContract } from '../model/product_contract.js';
import { CheckoutDrawerShell } from './checkout_drawer_shell.js';
import { CheckoutEnvProvider } from './env_context.js';
import { OfferCheckout } from './offer_checkout.js';
import type {
  CheckoutDrawerProps,
  OfferCheckoutProps,
  RequestCheckoutProps,
} from './checkout_subject.js';

/**
 * O organismo de checkout: recebe UM produto e um MODO (`subject`) e monta a
 * tela que o produto dita — a compra online de uma oferta (`offer`) ou o pedido
 * à equipe VOLL (`request`). As seções de viajantes e corporativa (rateio,
 * motivo, justificativa, observação) são as mesmas nos dois modos e ficam
 * separadas do produto.
 *
 * Resolve o roster antes de tudo. Oferta de voo sem `travelers` falha fechado
 * sem pedir perfil nenhum. Um bloco persistido antes de todo produtor emitir o
 * roster resolve o solicitante primeiro (`useRequesterFallback`) — no MESMO
 * componente de modo, para o diálogo não remontar quando o solicitante chega.
 */
// Sobrecargas por modo (e não só a união): o discriminante mora em
// `subject.mode`, aninhado, e com uma união de props o TS não daria ao
// `onSubmit` do host o envio do modo que ele escreveu.
export function CheckoutDrawer(props: Readonly<OfferCheckoutProps>): ReactNode;
// eslint-disable-next-line @typescript-eslint/unified-signatures -- a assinatura unida perde o tipo contextual do `onSubmit` (ver acima)
export function CheckoutDrawer(props: Readonly<RequestCheckoutProps>): ReactNode;
export function CheckoutDrawer(props: Readonly<CheckoutDrawerProps>): ReactNode {
  const { travelers, env } = props;
  const hasRoster = travelers !== undefined && travelers.length > 0;
  const product = productOf(props);
  const failClosed = isOffer(props) && productContract(props.subject.item).requiresRoster;
  // Só a oferta resolve o solicitante: é ela que reabre blocos do histórico. O
  // pedido sem viajantes (o `/me` não resolveu) fica carregando, sem enviar em
  // nome de alguém que ninguém escolheu.
  const resolvesRequester = isOffer(props) && !failClosed;
  const fallback = useRequesterFallback(
    env.io,
    props.open && !hasRoster && resolvesRequester,
    product,
  );

  if (!hasRoster && failClosed && isOffer(props)) {
    return props.open ? (
      <CheckoutEnvProvider env={env}>
        <RosterlessShell {...props} />
      </CheckoutEnvProvider>
    ) : null;
  }
  const roster = hasRoster
    ? { travelers, rosterFailed: false }
    : rosterlessRoster(resolvesRequester, fallback);
  return (
    <CheckoutEnvProvider env={env}>
      {isOffer(props) ? (
        <OfferCheckout {...props} {...roster} />
      ) : (
        <RequestCheckout {...props} {...roster} />
      )}
    </CheckoutEnvProvider>
  );
}

/** Oferta sem roster de um produto que o exige: a casca no erro, sem pedir
 *  perfil nenhum. */
function RosterlessShell({ subject, onClose }: Readonly<OfferCheckoutProps>) {
  const { item, items } = subject;
  return (
    <CheckoutDrawerShell
      phase="error"
      items={items ?? [item]}
      total={item.price}
      submitting={false}
      canSubmit={false}
      onSubmit={() => undefined}
      onClose={onClose}
    >
      {null}
    </CheckoutDrawerShell>
  );
}

/** Sem roster na oferta: o solicitante sozinho, dono do rateio — o farol de
 *  orçamento que esses cards sempre mostraram; com um bloco único, os dois
 *  modos de rateio enviam igual. No pedido: ninguém. */
function rosterlessRoster(
  resolvesRequester: boolean,
  fallback: { requester?: CheckoutTravelerRef; failed: boolean },
) {
  if (!resolvesRequester) return { travelers: [], rosterFailed: false };
  return {
    travelers: fallback.requester ? [fallback.requester] : [],
    ...(fallback.requester ? { requesterId: fallback.requester.id } : {}),
    allocationOwner: 'requester' as const,
    rosterFailed: fallback.failed,
  };
}

/** O discriminante mora em `subject.mode`, um nível abaixo: o TS não estreita
 *  `props` por ele sozinho. */
function isOffer(props: Readonly<CheckoutDrawerProps>): props is Readonly<OfferCheckoutProps> {
  return props.subject.mode === 'offer';
}

function productOf(props: Readonly<CheckoutDrawerProps>): CheckoutProductType {
  return isOffer(props) ? props.subject.item.productType : props.subject.request.productType;
}

/** Bloco sem `travelers` (carro, ônibus e mobilidade emitidos antes de o agents
 *  mandar o roster, reabertos do histórico): o form de reserva pedido SEM
 *  viajante é o de quem está logado, e o id dele vira o roster de um viajante
 *  só. Desligado (`enabled` falso) não pede nada. */
function useRequesterFallback(
  port: CheckoutIo | null,
  enabled: boolean,
  productType: CheckoutProductType,
): { requester?: CheckoutTravelerRef; failed: boolean } {
  const [state, setState] = useState<{ requester?: CheckoutTravelerRef; failed: boolean }>({
    failed: false,
  });

  // Sem port não há a quem pedir: a falha é do render, não de um efeito.
  if (enabled && !port && !state.failed) setState({ failed: true });

  useEffect(() => {
    if (!enabled || !port) return undefined;
    let cancelled = false;
    port
      .getBookingForm(productType)
      .then((form) => {
        if (cancelled) return;
        const id = form.traveler?.id;
        setState(
          id
            ? { requester: { id, name: form.traveler?.name ?? '' }, failed: false }
            : { failed: true },
        );
      })
      .catch(() => {
        if (!cancelled) setState({ failed: true });
      });
    return () => {
      cancelled = true;
      // Limpo ao fechar, não ao reabrir: reaberto, o primeiro render já nasce
      // sem o solicitante anterior (nem fetch dele, nem erro piscando).
      setState({ failed: false });
    };
  }, [enabled, productType, port]);

  return state;
}
